import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, MapPin, Camera, CheckCircle, ExternalLink, XCircle, AlertTriangle, MessageSquare, Send, Wrench, UserPlus, Clock, Play, FileText, Phone, Wifi, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TicketCard from '../components/TicketCard';
import TopBarTasksButton from '../components/tasks/TopBarTasksButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Check if scheduled date has passed
const isDatePassed = (dateString) => {
  if (!dateString) return false;
  const scheduledDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return scheduledDate < today;
};

const CONNECTION_STATUS = {
  scheduled: { label: 'Planlanıb', color: 'bg-purple-100 text-purple-700' },
  in_progress: { label: 'İcrada', color: 'bg-orange-100 text-orange-700' },
  completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700' },
};

const REJECTION_REASONS = [
  { value: 'no_space', label: 'Qutuda Yer Yoxdur' },
  { value: 'no_network', label: 'Şəbəkə Mövcud Deyil' },
  { value: 'wrong_technician', label: 'Texnik Düz Təyin Edilməyib' },
  { value: 'customer_refused', label: 'Müştəri İmtina Etdi' },
];

export default function TechnicianDashboard({ user, onLogout }) {
  const [tickets, setTickets] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('services'); // services, connections
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineTicket, setDeclineTicket] = useState(null);
  const [declineInfo, setDeclineInfo] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [commentTicket, setCommentTicket] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [ticketComments, setTicketComments] = useState([]);
  
  // Connection rejection states
  const [showConnectionRejectDialog, setShowConnectionRejectDialog] = useState(false);
  const [selectedConnectionForReject, setSelectedConnectionForReject] = useState(null);
  const [connectionRejectReason, setConnectionRejectReason] = useState('');
  const [connectionRejectNote, setConnectionRejectNote] = useState('');

  const token = localStorage.getItem('token');

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
    } catch (error) {
      toast.error('Ticketləri yükləmək mümkün olmadı');
    }
  };
  
  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${API}/connections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnections(response.data.connections || []);
    } catch (error) {
      console.error('Connections fetch error:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([fetchTickets(), fetchConnections()]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      fetchTickets();
      fetchConnections();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleNavigate = (ticket) => {
    if (ticket.latitude && ticket.longitude) {
      const wazeUrl = `https://waze.com/ul?ll=${ticket.latitude},${ticket.longitude}&navigate=yes`;
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${ticket.latitude},${ticket.longitude}`;
      
      // Try Waze first, fallback to Google Maps
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        window.open(wazeUrl, '_blank');
        setTimeout(() => {
          window.open(googleMapsUrl, '_blank');
        }, 2000);
      } else {
        window.open(googleMapsUrl, '_blank');
      }
    } else {
      toast.error('Koordinatlar mövcud deyil');
    }
  };

  const handleNavigateConnection = (conn) => {
    // Check for google_maps_url first
    if (conn.google_maps_url) {
      window.open(conn.google_maps_url, '_blank');
      return;
    }
    // Fallback to coordinates if available
    if (conn.latitude && conn.longitude) {
      const wazeUrl = `https://waze.com/ul?ll=${conn.latitude},${conn.longitude}&navigate=yes`;
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${conn.latitude},${conn.longitude}`;
      
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        window.open(wazeUrl, '_blank');
        setTimeout(() => {
          window.open(googleMapsUrl, '_blank');
        }, 2000);
      } else {
        window.open(googleMapsUrl, '_blank');
      }
    } else {
      toast.error('Xəritə linki mövcud deyil');
    }
  };

  const handleStartWork = async (ticketId) => {
    try {
      await axios.patch(
        `${API}/tickets/${ticketId}`,
        { status: 'in_progress' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('İş başladı');
      fetchTickets();
    } catch (error) {
      toast.error('Status yeniləmək mümkün olmadı');
    }
  };

  const handleUploadImage = async (ticketId, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/tickets/${ticketId}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Şəkil yükləndi');
      fetchTickets();
    } catch (error) {
      toast.error('Şəkil yükləmək mümkün olmadı');
    } finally {
      setUploading(false);
    }
  };

  const handleCompleteTicket = async (ticketId) => {
    try {
      await axios.patch(
        `${API}/tickets/${ticketId}`,
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket tamamlandı');
      fetchTickets();
      setSelectedTicket(null);
    } catch (error) {
      toast.error('Tamamlamaq mümkün olmadı');
    }
  };

  const handleShowDeclineDialog = async (ticket) => {
    try {
      const response = await axios.get(`${API}/tickets/${ticket.id}/decline-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeclineInfo(response.data);
      setDeclineTicket(ticket);
      setDeclineReason('');
      setShowDeclineDialog(true);
    } catch (error) {
      toast.error('Məlumat almaq mümkün olmadı');
    }
  };

  const handleDeclineTicket = async () => {
    if (!declineTicket) return;
    
    try {
      await axios.patch(
        `${API}/tickets/${declineTicket.id}`,
        { status: 'declined', decline_reason: declineReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const penaltyMsg = declineInfo?.current_penalty > 0 
        ? `(-${declineInfo.current_penalty} xal)` 
        : '(xal itkisi yoxdur)';
      
      toast.success(`Ticketdən imtina edildi ${penaltyMsg}`);
      fetchTickets();
      setShowDeclineDialog(false);
      setDeclineTicket(null);
      setDeclineReason('');
    } catch (error) {
      toast.error('İmtina etmək mümkün olmadı');
    }
  };

  // Comment functions
  const handleShowCommentDialog = async (ticket) => {
    setCommentTicket(ticket);
    setNewComment('');
    setShowCommentDialog(true);
    try {
      const response = await axios.get(`${API}/tickets/${ticket.id}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketComments(response.data);
    } catch (error) {
      setTicketComments([]);
    }
  };

  const handleAddComment = async () => {
    if (!commentTicket || !newComment.trim()) return;
    
    try {
      await axios.post(
        `${API}/tickets/${commentTicket.id}/comments`,
        { text: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Şərh əlavə edildi');
      setNewComment('');
      // Refresh comments
      const response = await axios.get(`${API}/tickets/${commentTicket.id}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketComments(response.data);
    } catch (error) {
      toast.error('Şərh əlavə etmək mümkün olmadı');
    }
  };

  const activeTickets = tickets
    .filter(t => ['assigned', 'in_progress'].includes(t.status))
    .sort((a, b) => {
      // First: in_progress tickets come first
      if (a.status === 'in_progress' && b.status !== 'in_progress') return -1;
      if (a.status !== 'in_progress' && b.status === 'in_progress') return 1;
      // Then: sort by scheduled_date from oldest to newest (earliest date first)
      // Tickets without scheduled_date go to the end
      if (a.scheduled_date && !b.scheduled_date) return -1;
      if (!a.scheduled_date && b.scheduled_date) return 1;
      if (a.scheduled_date && b.scheduled_date) {
        return new Date(a.scheduled_date) - new Date(b.scheduled_date);
      }
      // If neither has scheduled_date, sort by created_at
      return new Date(a.created_at) - new Date(b.created_at);
    });
  const completedTickets = tickets.filter(t => ['completed', 'closed'].includes(t.status));
  const overdueTickets = activeTickets.filter(t => isDatePassed(t.scheduled_date));
  
  // Sorted connections - in_progress first, then by scheduled_date
  const sortedConnections = [...connections].sort((a, b) => {
    // First: in_progress connections come first
    if (a.status === 'in_progress' && b.status !== 'in_progress') return -1;
    if (a.status !== 'in_progress' && b.status === 'in_progress') return 1;
    // Then: sort by scheduled_date from oldest to newest
    if (a.scheduled_date && !b.scheduled_date) return -1;
    if (!a.scheduled_date && b.scheduled_date) return 1;
    if (a.scheduled_date && b.scheduled_date) {
      return new Date(a.scheduled_date) - new Date(b.scheduled_date);
    }
    return new Date(a.created_at) - new Date(b.created_at);
  });
  const overdueConnections = connections.filter(c => isDatePassed(c.scheduled_date) && ['scheduled', 'in_progress'].includes(c.status));

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Yüklənir...</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-tight text-zinc-900" data-testid="technician-dashboard-title">
                Texnik Paneli
              </h1>
              <p className="text-sm text-zinc-600 mt-1">Salam, {user.full_name || user.username}</p>
            </div>
            <div className="flex items-center gap-2">
              <TopBarTasksButton />
              <Button
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Tab Navigation */}
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <div className="flex gap-2 mb-4 border-b pb-4">
          <Button
            onClick={() => setMainTab('services')}
            variant={mainTab === 'services' ? 'default' : 'outline'}
            className={mainTab === 'services' ? 'bg-blue-600 hover:bg-blue-700' : ''}
            data-testid="main-tab-services"
          >
            <Wrench className="h-4 w-4 mr-2" />
            Servislərim
            <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs">{activeTickets.length}</span>
          </Button>
          <Button
            onClick={() => { setMainTab('connections'); fetchConnections(); }}
            variant={mainTab === 'connections' ? 'default' : 'outline'}
            className={mainTab === 'connections' ? 'bg-green-600 hover:bg-green-700' : ''}
            data-testid="main-tab-connections"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Qoşulmalarım
            <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs">{connections.length}</span>
          </Button>
        </div>
      </div>

      {/* Services (Tickets) Content */}
      {mainTab === 'services' && (
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Overdue Tickets Warning */}
        {overdueTickets.length > 0 && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <div>
                <h3 className="font-bold text-red-700">Gecikmiş İşlər!</h3>
                <p className="text-red-600 text-sm">{overdueTickets.length} iş planlanmış tarixdən keçib. Zəhmət olmasa tamamlayın.</p>
              </div>
            </div>
          </div>
        )}

        {/* Active Tickets */}
        <section className="mb-8">
          <h2 className="text-xl font-bold mb-4 text-zinc-900" data-testid="active-tickets-title">
            Mənim İşlərim ({activeTickets.length})
          </h2>
          {activeTickets.length === 0 ? (
            <div className="bg-white p-8 rounded-lg border border-zinc-200 text-center">
              <p className="text-zinc-500">Sizə təyin olunmuş iş yoxdur</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeTickets.map(ticket => (
                <div key={ticket.id} className={`bg-white rounded-lg border p-4 ${isDatePassed(ticket.scheduled_date) ? 'border-red-300 bg-red-50' : 'border-zinc-200'}`}>
                  <TicketCard ticket={ticket} hideTechnician={true} />
                  
                  {/* Scheduled Date/Time Display */}
                  {(ticket.scheduled_date || ticket.scheduled_time) && (
                    <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${isDatePassed(ticket.scheduled_date) ? 'bg-red-100 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">
                        Planlanmış: {ticket.scheduled_date} {ticket.scheduled_time && `saat ${ticket.scheduled_time}`}
                      </span>
                      {isDatePassed(ticket.scheduled_date) && (
                        <Badge className="bg-red-600 text-white ml-auto">Gecikib!</Badge>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-4 space-y-2">
                    {ticket.latitude && ticket.longitude && (
                      <Button
                        onClick={() => handleNavigate(ticket)}
                        className="w-full bg-primary hover:bg-primary/90 text-white"
                        data-testid={`navigate-button-${ticket.id}`}
                      >
                        <MapPin className="h-4 w-4 mr-2" />
                        Xəritə ilə get
                      </Button>
                    )}
                    
                    {ticket.status === 'assigned' && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleStartWork(ticket.id)}
                          variant="outline"
                          className="flex-1"
                          data-testid={`start-work-button-${ticket.id}`}
                        >
                          İşə başla
                        </Button>
                        <Button
                          onClick={() => handleShowDeclineDialog(ticket)}
                          variant="outline"
                          className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                          data-testid={`decline-button-${ticket.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          İmtina et
                        </Button>
                      </div>
                    )}
                    
                    {/* Action Buttons Row: Comment + Image Upload */}
                    <div className="flex gap-2 mb-3">
                      {/* Comment Button */}
                      <Button
                        onClick={() => handleShowCommentDialog(ticket)}
                        variant="outline"
                        className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                        data-testid={`comment-button-${ticket.id}`}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Şərh ({ticket.comments?.length || 0})
                      </Button>
                      
                      {/* Image Upload Button */}
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleUploadImage(ticket.id, e.target.files[0]);
                            }
                          }}
                          disabled={uploading}
                          data-testid={`upload-image-input-${ticket.id}`}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          disabled={uploading}
                          onClick={() => {}}
                          asChild
                          data-testid={`upload-image-button-${ticket.id}`}
                        >
                          <span>
                            <Camera className="h-4 w-4 mr-2" />
                            {uploading ? 'Yüklənir...' : `Şəkil (${ticket.images?.length || 0})`}
                          </span>
                        </Button>
                      </label>
                    </div>
                    
                    {/* Complete Work Button - Full Width Green */}
                    <Button
                      onClick={() => setSelectedTicket(ticket)}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`complete-work-button-${ticket.id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      İşi Tamamla
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

      </div>
      )}

      {/* Connections Content */}
      {mainTab === 'connections' && (
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Overdue Connections Warning */}
        {overdueConnections.length > 0 && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <div>
                <h3 className="font-bold text-red-700">Gecikmiş Qoşulmalar!</h3>
                <p className="text-red-600 text-sm">{overdueConnections.length} qoşulma planlanmış tarixdən keçib. Zəhmət olmasa tamamlayın.</p>
              </div>
            </div>
          </div>
        )}

        <section>
          <h2 className="text-xl font-bold mb-4 text-zinc-900" data-testid="my-connections-title">
            Qoşulmalarım ({connections.length})
          </h2>
          {connections.length === 0 ? (
            <div className="bg-white p-8 rounded-lg border border-zinc-200 text-center">
              <p className="text-zinc-500">Sizə təyin olunmuş qoşulma yoxdur</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedConnections.map(conn => {
                const statusConfig = CONNECTION_STATUS[conn.status] || CONNECTION_STATUS.scheduled;
                const isOverdue = isDatePassed(conn.scheduled_date) && ['scheduled', 'in_progress'].includes(conn.status);
                return (
                  <Card key={conn.id} className={`hover:shadow-md transition-shadow ${isOverdue ? 'border-red-300 bg-red-50' : ''}`}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{conn.customer_name}</CardTitle>
                          <p className="text-sm text-zinc-500">{conn.connection_number}</p>
                        </div>
                        <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {/* Group */}
                      {conn.group_name && (
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-zinc-400" />
                          <span className="font-medium">{conn.group_name}</span>
                        </div>
                      )}
                      
                      {/* Phone */}
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-zinc-400" />
                        <a href={`tel:${conn.customer_phone}`} className="text-blue-600 hover:underline">
                          {conn.customer_phone}
                        </a>
                        {conn.customer_phone_2 && (
                          <span className="text-zinc-500">
                            / <a href={`tel:${conn.customer_phone_2}`} className="text-blue-600 hover:underline">{conn.customer_phone_2}</a>
                          </span>
                        )}
                      </div>
                      
                      {/* Service Type */}
                      {conn.service_type && (
                        <div className="flex items-center gap-2 text-sm">
                          <Wifi className="h-4 w-4 text-zinc-400" />
                          <span>{conn.service_type.split('+').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}</span>
                        </div>
                      )}
                      
                      {/* Address */}
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-zinc-400" />
                        <span>{conn.street} {conn.building}/{conn.apartment}</span>
                      </div>
                      
                      {/* Scheduled Date/Time with overdue indicator */}
                      {conn.scheduled_date && (
                        <div className={`flex items-center gap-2 text-sm p-2 rounded ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                          <Clock className="h-4 w-4" />
                          <span className="font-medium">
                            Planlanmış: {conn.scheduled_date} {conn.scheduled_time && `saat ${conn.scheduled_time}`}
                          </span>
                          {isOverdue && (
                            <Badge className="bg-red-600 text-white ml-auto">Gecikib!</Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Created at */}
                      <div className="flex items-center gap-2 text-sm text-zinc-500">
                        <Clock className="h-4 w-4" />
                        <span>{new Date(conn.created_at).toLocaleString('az-AZ', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })} qeydə alınıb</span>
                      </div>
                      
                      {/* Notes preview */}
                      {conn.notes && (
                        <div className="flex items-center gap-2 text-sm text-zinc-600">
                          <FileText className="h-4 w-4 text-zinc-400" />
                          <span className="truncate">{conn.notes.length > 50 ? conn.notes.substring(0, 50) + '...' : conn.notes}</span>
                        </div>
                      )}
                      
                      <div className="mt-4 space-y-2">
                        {/* Navigate Button */}
                        <Button
                          onClick={() => handleNavigateConnection(conn)}
                          className="w-full bg-primary hover:bg-primary/90 text-white"
                          data-testid={`navigate-connection-${conn.id}`}
                        >
                          <MapPin className="h-4 w-4 mr-2" />
                          Xəritə ilə get
                        </Button>
                      
                      <div className="flex gap-2">
                        {conn.status === 'scheduled' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-orange-600 hover:bg-orange-700"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/connections/${conn.id}/status/in_progress`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Qoşulma işi başladı');
                                  fetchConnections();
                                } catch (error) {
                                  toast.error('Status dəyişmək mümkün olmadı');
                                }
                              }}
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Başla
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedConnectionForReject(conn);
                                setConnectionRejectReason('');
                                setConnectionRejectNote('');
                                setShowConnectionRejectDialog(true);
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              İmtina
                            </Button>
                          </>
                        )}
                        {conn.status === 'in_progress' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/connections/${conn.id}/status/completed`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Qoşulma tamamlandı');
                                  fetchConnections();
                                } catch (error) {
                                  toast.error('Status dəyişmək mümkün olmadı');
                                }
                              }}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Tamamla
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedConnectionForReject(conn);
                                setConnectionRejectReason('');
                                setConnectionRejectNote('');
                                setShowConnectionRejectDialog(true);
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              İmtina
                            </Button>
                          </>
                        )}
                      </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </div>
      )}

      {/* Ticket Details Dialog */}
      {selectedTicket && (
        <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ticket-details-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Ticket Detalları</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-zinc-600">Abunəçi</p>
                <p className="font-semibold">{selectedTicket.subscriber_name}</p>
                <p className="text-sm">{selectedTicket.subscriber_phone}</p>
              </div>
              
              <div>
                <p className="text-sm text-zinc-600">Ünvan</p>
                <p className="font-semibold">{selectedTicket.subscriber_location}</p>
              </div>
              
              <div>
                <p className="text-sm text-zinc-600">Problem</p>
                <p className="font-semibold">{selectedTicket.problem_description}</p>
              </div>
              
              {selectedTicket.images && selectedTicket.images.length > 0 && (
                <div>
                  <p className="text-sm text-zinc-600 mb-2">Şəkillər</p>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedTicket.images.map((img, idx) => (
                      <img key={idx} src={img} alt={`Upload ${idx + 1}`} className="rounded-lg w-full" />
                    ))}
                  </div>
                </div>
              )}
              
              {selectedTicket.status === 'in_progress' && (
                <Button
                  onClick={() => handleCompleteTicket(selectedTicket.id)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  data-testid="complete-ticket-button"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  İş tamamlandı
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Decline Confirmation Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-zinc-900">
              Ticketdən imtina
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Penalty Warning */}
            <div className={`p-4 rounded-lg border ${
              declineInfo?.current_penalty === 0 
                ? 'bg-green-50 border-green-200' 
                : declineInfo?.current_penalty === 5 
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`h-5 w-5 mt-0.5 ${
                  declineInfo?.current_penalty === 0 
                    ? 'text-green-600' 
                    : declineInfo?.current_penalty === 5 
                      ? 'text-yellow-600'
                      : 'text-red-600'
                }`} />
                <div>
                  <p className={`font-semibold ${
                    declineInfo?.current_penalty === 0 
                      ? 'text-green-700' 
                      : declineInfo?.current_penalty === 5 
                        ? 'text-yellow-700'
                        : 'text-red-700'
                  }`}>
                    {declineInfo?.current_penalty === 0 
                      ? 'Xal itkisi yoxdur' 
                      : `-${declineInfo?.current_penalty} xal itirəcəksiniz`}
                  </p>
                  <p className="text-sm text-zinc-600 mt-1">{declineInfo?.message}</p>
                  <p className="text-xs text-zinc-500 mt-2">
                    Ticket təyin olunandan {declineInfo?.hours_elapsed} saat keçib
                  </p>
                </div>
              </div>
            </div>
            
            {/* Decline Reason */}
            <div>
              <Label htmlFor="decline_reason">İmtina səbəbi (istəyə bağlı)</Label>
              <Input
                id="decline_reason"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Məsələn: Ünvan tapılmadı, Müştəri evdə deyil..."
                className="mt-1.5"
                data-testid="decline-reason-input"
              />
            </div>
            
            {/* Time-based scoring rules */}
            <div className="bg-zinc-100 p-3 rounded-lg">
              <p className="text-xs font-semibold text-zinc-700 mb-2">İmtina xallanma qaydaları:</p>
              <ul className="text-xs text-zinc-600 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-12 text-green-600 font-semibold">0 xal</span>
                  <span>1 saat ərzində imtina</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-12 text-yellow-600 font-semibold">-5 xal</span>
                  <span>1-12 saat ərzində imtina</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-12 text-red-600 font-semibold">-20 xal</span>
                  <span>12 saatdan sonra imtina</span>
                </li>
              </ul>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeclineDialog(false)}
              data-testid="cancel-decline-button"
            >
              Ləğv et
            </Button>
            <Button
              onClick={handleDeclineTicket}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-decline-button"
            >
              <XCircle className="h-4 w-4 mr-2" />
              İmtina et {declineInfo?.current_penalty > 0 && `(-${declineInfo.current_penalty} xal)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={showCommentDialog} onOpenChange={setShowCommentDialog}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Şərhlər - #{commentTicket?.id.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Existing Comments */}
            <div className="max-h-60 overflow-y-auto space-y-3">
              {ticketComments.length === 0 ? (
                <p className="text-center text-zinc-500 py-4">Hələ şərh yoxdur</p>
              ) : (
                ticketComments.map((comment) => (
                  <div key={comment.id} className="bg-zinc-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-sm text-zinc-900">
                        {comment.full_name || comment.username}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {new Date(comment.created_at).toLocaleString('az-AZ')}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-700">{comment.text}</p>
                  </div>
                ))
              )}
            </div>
            
            {/* New Comment Input */}
            <div className="space-y-2">
              <Label>Yeni şərh</Label>
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Şərhinizi yazın..."
                className="min-h-20"
                data-testid="new-comment-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCommentDialog(false)}
            >
              Bağla
            </Button>
            <Button
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-comment-button"
            >
              <Send className="h-4 w-4 mr-2" />
              Göndər
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connection Reject Dialog */}
      <Dialog open={showConnectionRejectDialog} onOpenChange={setShowConnectionRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-zinc-900">
              Qoşulmadan İmtina
            </DialogTitle>
            <DialogDescription>
              İmtina səbəbini seçin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>İmtina Səbəbi *</Label>
              <Select value={connectionRejectReason} onValueChange={setConnectionRejectReason}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Səbəb seçin" />
                </SelectTrigger>
                <SelectContent>
                  {REJECTION_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {connectionRejectReason === 'customer_refused' && (
              <div>
                <Label>Açıqlama *</Label>
                <Textarea 
                  value={connectionRejectNote}
                  onChange={(e) => setConnectionRejectNote(e.target.value)}
                  placeholder="Müştərinin imtina səbəbini yazın..."
                  rows={3}
                  required
                />
              </div>
            )}
            
            {connectionRejectReason && connectionRejectReason !== 'customer_refused' && (
              <div>
                <Label>Əlavə qeyd (ixtiyari)</Label>
                <Textarea 
                  value={connectionRejectNote}
                  onChange={(e) => setConnectionRejectNote(e.target.value)}
                  placeholder="Əlavə məlumat..."
                  rows={2}
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConnectionRejectDialog(false)}
            >
              Ləğv et
            </Button>
            <Button
              onClick={async () => {
                if (!connectionRejectReason) {
                  toast.error('Səbəb seçin');
                  return;
                }
                if (connectionRejectReason === 'customer_refused' && !connectionRejectNote.trim()) {
                  toast.error('Müştəri imtina etdikdə açıqlama yazılmalıdır');
                  return;
                }
                try {
                  await axios.post(`${API}/connections/${selectedConnectionForReject.id}/reject`, {
                    reason: connectionRejectReason,
                    note: connectionRejectNote
                  }, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success('Qoşulma imtina edildi');
                  setShowConnectionRejectDialog(false);
                  fetchConnections();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Xəta baş verdi');
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <XCircle className="h-4 w-4 mr-2" />
              İmtina Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
